import type {
  GamificationHeroEvent,
  GamificationUpdates,
  TrophyId,
} from '@crowdfpl/domain/gamification';
import type {
  GamificationMeState,
  GamificationPrivatePinned,
  GamificationPublicState,
  SetGamificationPinResponse,
} from '@/types/gamification';

export type GamificationSeenSnapshot = Readonly<{
  updates: GamificationUpdates;
  newTrophyIds: readonly TrophyId[];
}>;

function cloneHeroEvent(heroEvent: GamificationHeroEvent): GamificationHeroEvent {
  if (!heroEvent) {
    return null;
  }

  return {
    ...heroEvent,
    share_payload: heroEvent.share_payload ? { ...heroEvent.share_payload } : null,
  };
}

function cloneUpdates(updates: GamificationUpdates): GamificationUpdates {
  return {
    ...updates,
    hero_event: cloneHeroEvent(updates.hero_event),
  };
}

function toPrivatePinned(pin: SetGamificationPinResponse): GamificationPrivatePinned {
  return {
    trophy_id: pin?.trophy_id ?? null,
    source: pin?.source ?? null,
  };
}

export function applyPinnedToMyGamification(
  state: GamificationMeState,
  pin: SetGamificationPinResponse,
): GamificationMeState {
  return {
    ...state,
    pinned: toPrivatePinned(pin),
  };
}

export function applyPinnedToPublicGamification(
  state: GamificationPublicState,
  trophyId: TrophyId | null,
): GamificationPublicState {
  return {
    ...state,
    pinned: {
      trophy_id: trophyId,
    },
  };
}

export function createSeenSnapshot(state: GamificationMeState | undefined): GamificationSeenSnapshot | undefined {
  if (!state) {
    return undefined;
  }

  return {
    updates: cloneUpdates(state.updates),
    newTrophyIds: state.trophies.filter(trophy => trophy.is_new).map(trophy => trophy.trophy_id),
  };
}

export function applyOptimisticSeenToMyGamification(state: GamificationMeState): GamificationMeState {
  if (!state.updates.has_unseen && state.updates.count === 0) {
    return state;
  }

  return {
    ...state,
    // is_new derives from the same unseen set as has_unseen — clear both on modal open
    trophies: state.trophies.some(trophy => trophy.is_new)
      ? state.trophies.map(trophy => (trophy.is_new ? { ...trophy, is_new: false } : trophy))
      : state.trophies,
    updates: {
      ...state.updates,
      has_unseen: false,
      count: 0,
      hero_event: state.updates.hero_event,
    },
  };
}

export function restoreSeenSnapshot(
  state: GamificationMeState,
  snapshot: GamificationSeenSnapshot,
): GamificationMeState {
  const newTrophyIds = new Set(snapshot.newTrophyIds);
  return {
    ...state,
    trophies: newTrophyIds.size
      ? state.trophies.map(trophy =>
          newTrophyIds.has(trophy.trophy_id) ? { ...trophy, is_new: true } : trophy,
        )
      : state.trophies,
    updates: cloneUpdates(snapshot.updates),
  };
}
